import type { VenueId } from '@beat-venues/common-types';
import { type VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import type { CommonProps } from '../../types';
import { Logo } from '../logo';

export interface LogoTagProps
    extends CommonProps,
        VariantProps<typeof LogoTagStyles> {
    venueId: VenueId | 'my-beat';
}

export const LogoTagStyles = cva(
    `rounded-full border-gray-700 flex items-center justify-center`,
    {
        variants: {
            size: {
                xs: 'border-none h-8 w-8',
                sm: 'border-none h-12 w-12',
                md: 'border-8 h-[76px] w-[76px]',
                lg: 'border-8 h-[76px] w-[76px] lg:border-[13px] lg:h-[103px] lg:w-[103px]',
                xl: 'border-8 h-[76px] w-[76px] lg:border-[16px] lg:h-[140px] lg:w-[140px]',
            },
        },
        defaultVariants: {
            size: 'md',
        },
    }
);

export const LogoImgStyles = cva(``, {
    variants: {
        size: {
            xs: 'h-4 w-4',
            sm: 'h-6 w-6',
            md: 'h-8 w-8',
            lg: 'h-8 w-8 lg:h-10 lg:w-10',
            xl: 'h-8 w-8 lg:h-[64px] lg:w-[64px]',
        },
    },
    defaultVariants: {
        size: 'md',
    },
});

export const LogoTag = ({
    className,
    venueId,
    size,
    testId = 'logo-tag',
    ...props
}: LogoTagProps): JSX.Element => {
    const backgroundClassNames = {
        beat: '',
        'my-beat': 'bg-white',
        sportpaleis: 'bg-as-brand-500',
        'afas-dome': 'bg-ad-brand-500',
        'lotto-arena': 'bg-la-brand-500',
        'forest-national': 'bg-vn-brand-500',
        'trixxo-arena': 'bg-ta-brand-500',
        'stadsschouwburg-antwerp': 'bg-sa-brand-500',
        'capitole-ghent': 'bg-cg-brand-500',
        'trixxo-theater': 'bg-tt-brand-500',
        'beat-tickets': 'bg-bt-brand-500',
        'channel-business': 'bg-bt-brand-500',
        'channel-corporate': 'bg-bt-brand-500',
    };

    const filterClassNames = {
        beat: '',
        'my-beat': 'brightness-0 invert-0',
        sportpaleis: 'brightness-0 invert',
        'afas-dome': 'brightness-0 invert',
        'lotto-arena': 'brightness-0 invert',
        'forest-national': 'brightness-0 invert',
        'trixxo-arena': 'brightness-0 invert',
        'stadsschouwburg-antwerp': 'brightness-0 invert',
        'capitole-ghent': 'brightness-0 invert-0',
        'trixxo-theater': 'brightness-0 invert',
        'beat-tickets': 'brightness-0 invert',
        'channel-business': 'brightness-0 invert',
        'channel-corporate': 'brightness-0 invert',
    };

    const backgroundClassName = backgroundClassNames[venueId];
    const filterClassName = filterClassNames[venueId];
    return (
        <div
            className={twMerge(
                LogoTagStyles({ className, size }),
                backgroundClassName
            )}
            {...props}
            data-testid={testId}
        >
            <Logo
                className={twMerge(LogoImgStyles({ size }), filterClassName)}
                color="white"
                size="sm"
                venueId={venueId}
            />
        </div>
    );
};
